import { inject, Injectable, signal } from '@angular/core';
import { HomeService } from '@app/home/home.service';
import { AssetsService } from './assets.service';
import { HelperService } from './helper.service';
import { Banner } from '../models/assets.model';
import { User } from '../models/user.model';
import { Theming, ThemingService } from './theming.service';
import { lastValueFrom } from 'rxjs';
import { TournamentsService } from '../modals/live-ops/tournaments/tournaments.service';
import { UserRepository } from '@store/user/user.repository';
import { PreloadAssets, FONTS_TO_LOAD } from '@shared/utils/preloader-assets-list';
import { LoggerService } from './logger.service';
import { BannerService } from './banner.service';

@Injectable({ providedIn: 'root' })
export class PreLoaderService {
  private logger = inject(LoggerService);
  private homeService = inject(HomeService);
  private assetsService = inject(AssetsService);
  private helper = inject(HelperService);
  private userRepo = inject(UserRepository);
  private themingService = inject(ThemingService);
  private tournamentService = inject(TournamentsService);
  private readonly bannerService = inject(BannerService);

  readonly hasError = signal(false);
  readonly completed = signal(false);
  readonly retriesExhausted = signal(false);

  private initialized = false;
  private failureCount = 0;
  private readonly MAX_RETRIES = 3;
  private images: readonly string[] = [];
  private banners: Banner[] = [];
  private theming!: Theming;

  private get user(): User {
    return this.userRepo.getUser();
  }

  async init(): Promise<void> {
    if (this.initialized) return;
    this.initialized = true;
    this.hasError.set(false);
    this.completed.set(false);

    try {
      await Promise.all([this.initAssets(), this.initTheming(), this.initBanners()]);

      await this.homeService.init();

      this.images = PreloadAssets.getAll(this.themingService, this.theming, this.banners);

      await this.loadEssentialAssets();

      await Promise.all([this.tournamentService.init(), this.loadFonts()]);

      this.failureCount = 0;
      this.completed.set(true);
    } catch (err) {
      this.logger.error('PreLoaderService: Essential initialization failed', err);
      this.initialized = false;
      this.failureCount++;

      if (this.failureCount > this.MAX_RETRIES) {
        this.retriesExhausted.set(true);
      } else {
        this.hasError.set(true);
      }
    }
  }

  private async loadEssentialAssets(): Promise<void> {
    const promises = this.images.filter(Boolean).map(
      (image) =>
        new Promise<void>((resolve) => {
          const src = image.includes('cdn') ? image : this.helper.getCloudServiceCdn(image);
          const img = new Image();

          img.onload = () => resolve();
          img.onerror = () => resolve();
          img.src = src;
        })
    );

    await Promise.all(promises);
  }

  private async loadFonts(): Promise<void> {
    if (!('fonts' in document)) {
      return Promise.resolve();
    }

    try {
      const fontPromises = FONTS_TO_LOAD.map((font) => document.fonts.load(font));
      await Promise.allSettled(fontPromises);
    } catch (err) {
      this.logger.error('PreLoaderService: Unexpected error loading fonts', err);
    }
  }

  private async initAssets(): Promise<void> {
    await this.assetsService.initGamesIconVersion();
  }

  private async initBanners(): Promise<void> {
    this.banners = await this.bannerService.initBanners();
  }

  private async initTheming(): Promise<void> {
    this.theming = await lastValueFrom(this.themingService.initTheming(this.user.culture));

    await this.setPortalSprites();
  }

  private async setPortalSprites(): Promise<void> {
    await this.themingService.setPortalSprites();
  }
}
